import React from 'react';
import {
  LayoutDashboard,
  List,
  Bell,
  BarChart3,
  User,
  Settings,
  Bell as BellSmall,
  Users,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';

/**
 * Mockup visual del Dashboard PreventiApp.
 * No es un dashboard funcional — es ilustración decorativa para el hero.
 * Renderizado 100% con SVG/HTML/CSS, ligero y responsive.
 */
const DashboardMockup = () => {
  return (
    <div
      data-testid="hero-dashboard-mockup"
      className="relative w-full rounded-[28px] overflow-hidden border border-brand bg-white shadow-[0_40px_80px_-30px_rgba(11,27,94,0.35)]"
      style={{ aspectRatio: '4 / 3.4' }}
      aria-label="Vista previa del dashboard PreventiApp"
    >
      <div className="absolute inset-0 flex">
        {/* Sidebar */}
        <aside className="w-[68px] sm:w-[78px] bg-brand-deep flex flex-col items-center py-5 gap-4">
          <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#78A8FF]" />
          </div>
          <div className="mt-2 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-white" />
          </div>
          {[List, Bell, BarChart3, User, Settings].map((Icon, i) => (
            <div key={i} className="w-10 h-10 rounded-xl flex items-center justify-center text-white/55 hover:text-white">
              <Icon className="w-5 h-5" />
            </div>
          ))}
        </aside>

        {/* Main */}
        <div className="flex-1 p-4 sm:p-5 bg-white flex flex-col gap-3 sm:gap-4 overflow-hidden">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <h4 className="text-[15px] sm:text-base font-bold text-brand-deep">Resumen general</h4>
            <div className="px-3 py-1.5 rounded-lg border border-brand text-xs font-semibold text-brand-deep flex items-center gap-2">
              Este mes
              <svg viewBox="0 0 12 12" className="w-3 h-3"><path d="M3 4.5 L6 7.5 L9 4.5" stroke="#0B1B5E" strokeWidth="1.5" fill="none" /></svg>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            <KpiCard label="Casos registrados" value="128" tone="violet" icon={BellSmall} spark="violet" />
            <KpiCard label="Alertas activas" value="15" tone="coral" icon={Bell} spark="coral" />
            <KpiCard label="En seguimiento" value="32" tone="indigo" icon={Users} spark="indigo" />
            <KpiCard label="Casos cerrados" value="81" tone="mint" icon={CheckCircle2} spark="mint" />
          </div>

          {/* Chart + Donut */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 flex-1 min-h-0">
            <Panel title="Tendencia mensual">
              <LineChart />
            </Panel>
            <Panel title="Niveles de riesgo">
              <DonutLegend />
            </Panel>
          </div>

          {/* Bottom row */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Panel title="Dominios más observados" compact>
              <BarsList />
            </Panel>
            <Panel title="Observaciones recientes" compact>
              <RecentList />
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

const tones = {
  violet: { bg: '#EEEAFE', fg: '#5346E6' },
  coral: { bg: '#FFE0DA', fg: '#E25E48' },
  indigo: { bg: '#E0E4FA', fg: '#4C53C7' },
  mint: { bg: '#DFF6EC', fg: '#1E9F73' },
  amber: { bg: '#FFF1D6', fg: '#C98718' },
  sky: { bg: '#DCE7FF', fg: '#2F6BFF' },
  teal: { bg: '#D6F3F3', fg: '#00A6A6' },
};

const KpiCard = ({ label, value, tone = 'violet', icon: Icon, spark = 'violet' }) => {
  const t = tones[tone];
  return (
    <div className="rounded-xl border border-brand p-2 sm:p-2.5 bg-white">
      <div className="flex items-center justify-between">
        <span className="text-[9px] sm:text-[10px] font-semibold text-brand-muted leading-tight pr-1 truncate">{label}</span>
        <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center" style={{ background: t.bg, color: t.fg }}>
          <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      </div>
      <div className="mt-1 text-base sm:text-lg font-extrabold text-brand-deep leading-none">{value}</div>
      <Spark color={tones[spark].fg} />
    </div>
  );
};

const Spark = ({ color }) => (
  <svg viewBox="0 0 80 18" className="mt-1.5 w-full h-3">
    <polyline
      points="0,14 10,11 20,12 30,8 40,9 50,6 60,7 70,4 80,3"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Panel = ({ title, children, compact = false }) => (
  <div className={`rounded-xl border border-brand bg-white flex flex-col ${compact ? 'p-2.5' : 'p-3'} min-h-0`}>
    <h5 className="text-[10px] sm:text-[11px] font-bold text-brand-deep mb-1.5">{title}</h5>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

const LineChart = () => (
  <svg viewBox="0 0 200 80" className="w-full h-full" preserveAspectRatio="none">
    <defs>
      <linearGradient id="lineFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5346E6" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#5346E6" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* grid lines */}
    {[20, 40, 60].map((y) => (
      <line key={y} x1="0" y1={y} x2="200" y2={y} stroke="#EEF4FF" strokeWidth="1" />
    ))}
    <path d="M0,62 L40,50 L80,30 L120,42 L160,22 L200,12 L200,80 L0,80 Z" fill="url(#lineFill)" />
    <polyline
      points="0,62 40,50 80,30 120,42 160,22 200,12"
      fill="none"
      stroke="#5346E6"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {[0, 40, 80, 120, 160, 200].map((x, i) => (
      <circle key={i} cx={x} cy={[62, 50, 30, 42, 22, 12][i]} r="2.4" fill="#FFFFFF" stroke="#5346E6" strokeWidth="1.6" />
    ))}
  </svg>
);

const DonutLegend = () => (
  <div className="flex items-center gap-3 h-full">
    <svg viewBox="0 0 36 36" className="h-full w-auto max-h-[110px]">
      {/* Donut segments (stroke-dasharray) */}
      <circle cx="18" cy="18" r="14" fill="none" stroke="#EEF4FF" strokeWidth="6" />
      {/* Bajo 40% */}
      <circle cx="18" cy="18" r="14" fill="none" stroke="#28B487" strokeWidth="6" strokeDasharray="35.2 87.96" strokeDashoffset="0" transform="rotate(-90 18 18)" strokeLinecap="butt" />
      {/* Medio 42% */}
      <circle cx="18" cy="18" r="14" fill="none" stroke="#F6B73C" strokeWidth="6" strokeDasharray="36.96 87.96" strokeDashoffset="-35.2" transform="rotate(-90 18 18)" />
      {/* Alto 18% */}
      <circle cx="18" cy="18" r="14" fill="none" stroke="#E25E48" strokeWidth="6" strokeDasharray="15.84 87.96" strokeDashoffset="-72.16" transform="rotate(-90 18 18)" />
    </svg>
    <ul className="text-[10px] sm:text-[11px] space-y-1.5 flex-1">
      <LegendRow color="#E25E48" label="Alto" value="18%" />
      <LegendRow color="#F6B73C" label="Medio" value="42%" />
      <LegendRow color="#28B487" label="Bajo" value="40%" />
    </ul>
  </div>
);

const LegendRow = ({ color, label, value }) => (
  <li className="flex items-center justify-between gap-2">
    <span className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-brand-deep font-semibold">{label}</span>
    </span>
    <span className="text-brand-muted font-semibold">{value}</span>
  </li>
);

const BarsList = () => (
  <ul className="space-y-2 text-[10px] sm:text-[11px]">
    {[
      { label: 'Convivencia', pct: 45, color: '#5346E6' },
      { label: 'Participación', pct: 30, color: '#78A8FF' },
      { label: 'Ambiente escolar', pct: 25, color: '#00A6A6' },
    ].map((b) => (
      <li key={b.label}>
        <div className="flex items-center justify-between">
          <span className="text-brand-deep font-semibold">{b.label}</span>
          <span className="text-brand-muted font-semibold">{b.pct}%</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-brand-light overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.color }} />
        </div>
      </li>
    ))}
  </ul>
);

const RecentList = () => {
  const rows = [
    { dot: '#5346E6', label: 'Situación en recreo — 7°B', time: 'Hoy, 10:30', badge: 'Medio', badgeBg: '#FFF1D6', badgeFg: '#C98718' },
    { dot: '#E25E48', label: 'Conflicto verbal — 8°A', time: 'Hoy, 09:15', badge: 'Alto', badgeBg: '#FFE0DA', badgeFg: '#E25E48' },
    { dot: '#F6B73C', label: 'Acoso entre pares — 6°C', time: 'Ayer, 15:45', badge: 'Medio', badgeBg: '#FFF1D6', badgeFg: '#C98718' },
  ];
  return (
    <ul className="space-y-1.5 text-[10px] sm:text-[11px]">
      {rows.map((r) => (
        <li key={r.label} className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.dot }} />
            <span className="min-w-0">
              <span className="block text-brand-deep font-semibold truncate">{r.label}</span>
              <span className="block text-brand-muted">{r.time}</span>
            </span>
          </span>
          <span
            className="shrink-0 text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-md"
            style={{ background: r.badgeBg, color: r.badgeFg }}
          >
            {r.badge}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default DashboardMockup;
